import { formatEnvironment } from '@puffin/ui-library';
import { useQuery } from '@apollo/client/react';
import { getTestTurtleClient, turtleGql } from '@puffin/app-frame-api';
import { createRoute, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router';
import { lazy } from 'react';
import { rootRoute } from '../services/routing/rootRoute';
import { routeLabels, type RouteBreadcrumb } from '../services/routing/routeMetadata';

const TEST_RUN_BREADCRUMB_QUERY = turtleGql(`
  query TestRunBreadcrumb($id: ID!) {
    testRun(id: $id) {
      id
      environment
      testCase {
        id
      }
      testPlan {
        id
      }
    }
  }
`);

const useTestRunBreadcrumb = (runId: string) => {
  const { data } = useQuery(TEST_RUN_BREADCRUMB_QUERY, { client: getTestTurtleClient(), variables: { id: runId } });
  const run = data?.testRun;
  return run ? { planId: run.testPlan.id, caseName: run.testCase.id, environment: run.environment } : undefined;
};

const LazyPlanPage = lazy(async () => {
  const { PlanPage } = await import('@puffin/e2e-testing/plan-page');
  return { default: PlanPage };
});

const LazyCasePage = lazy(async () => {
  const { CasePage } = await import('@puffin/e2e-testing/case-page');
  return { default: CasePage };
});

const LazyRunPage = lazy(async () => {
  const { RunPage } = await import('@puffin/e2e-testing/run-page');
  return { default: RunPage };
});

const testCasesBreadcrumb: RouteBreadcrumb = { useLabel: () => 'E2E Test Cases' };
const testPlansBreadcrumb: RouteBreadcrumb = { useLabel: () => 'E2E Test Plans', to: '/tests/e2e/plans' };

const testsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'tests',
  component: Outlet,
  staticData: { ...routeLabels('Testing'), breadcrumbTo: '/tests/e2e/cases' },
});

const testsIndexRoute = createRoute({
  getParentRoute: () => testsRoute,
  path: '/',
  beforeLoad: () => redirect({ to: '/tests/e2e/cases', throw: true }),
});

const testCasesRoute = createRoute({
  getParentRoute: () => testsRoute,
  path: 'e2e/cases',
  component: lazyRouteComponent(() => import('@puffin/e2e-testing/cases-page'), 'CasesPage'),
  staticData: {
    ...routeLabels('E2E Test Cases'),
    navigationPath: '/tests/e2e/cases',
  },
});

const testCaseDetailRoute = createRoute(
  (() => {
    const useTestCasePageName = (): string => `Case: ${testCaseDetailRoute.useParams().caseId}`;
    const TestCasePage = () => <LazyCasePage caseId={testCaseDetailRoute.useParams().caseId} />;
    return {
      getParentRoute: () => testsRoute,
      path: 'e2e/cases/$caseId',
      component: TestCasePage,
      staticData: {
        useBreadcrumbLabel: useTestCasePageName,
        useDocumentTitlePrefix: useTestCasePageName,
        navigationPath: '/tests/e2e/cases',
        parentBreadcrumbs: [testCasesBreadcrumb],
      },
    };
  })(),
);

const testPlansRoute = createRoute({
  getParentRoute: () => testsRoute,
  path: 'e2e/plans',
  component: lazyRouteComponent(() => import('@puffin/e2e-testing/plans-page'), 'PlansPage'),
  staticData: {
    ...routeLabels('E2E Test Plans'),
    navigationPath: '/tests/e2e/plans',
  },
});

const testPlanDetailRoute = createRoute(
  (() => {
    const useTestPlanPageName = (): string => `Plan: ${testPlanDetailRoute.useParams().planId}`;
    const TestPlanPage = () => {
      const { planId } = testPlanDetailRoute.useParams();
      return <LazyPlanPage pageName={`Plan: ${planId}`} planId={planId} />;
    };
    return {
      getParentRoute: () => testsRoute,
      path: 'e2e/plans/$planId',
      component: TestPlanPage,
      staticData: {
        useBreadcrumbLabel: useTestPlanPageName,
        useDocumentTitlePrefix: useTestPlanPageName,
        navigationPath: '/tests/e2e/plans',
        parentBreadcrumbs: [testPlansBreadcrumb],
      },
    };
  })(),
);

const testRunDetailRoute = createRoute(
  (() => {
    const useTestRunPageName = (): string => {
      const { runId } = testRunDetailRoute.useParams();
      const run = useTestRunBreadcrumb(runId);
      return run ? `Run: ${run.caseName} (${formatEnvironment(run.environment)})` : `Run: ${runId}`;
    };
    const useTestRunPlanName = (): string => {
      const run = useTestRunBreadcrumb(testRunDetailRoute.useParams().runId);
      return run ? `Plan: ${run.planId}` : 'Plan';
    };
    const useTestRunPlanTo = (): `/tests/e2e/plans/${string}` | undefined => {
      const run = useTestRunBreadcrumb(testRunDetailRoute.useParams().runId);
      return run ? `/tests/e2e/plans/${encodeURIComponent(run.planId)}` : undefined;
    };
    const TestRunPage = () => <LazyRunPage runId={testRunDetailRoute.useParams().runId} />;
    return {
      getParentRoute: () => testsRoute,
      path: 'e2e/runs/$runId',
      component: TestRunPage,
      staticData: {
        useBreadcrumbLabel: useTestRunPageName,
        useDocumentTitlePrefix: useTestRunPageName,
        navigationPath: '/tests/e2e/plans',
        parentBreadcrumbs: [testPlansBreadcrumb, { useLabel: useTestRunPlanName, useTo: useTestRunPlanTo }],
      },
    };
  })(),
);

const startTestRoute = createRoute({
  getParentRoute: () => testsRoute,
  path: 'e2e/start',
  component: lazyRouteComponent(() => import('@puffin/e2e-testing/start-page'), 'StartPage'),
  staticData: { ...routeLabels('Start E2E Tests'), navigationPath: '/tests/e2e/start' },
});

const testTemplatesRoute = createRoute({
  getParentRoute: () => testsRoute,
  path: 'templates',
  validateSearch: (search: Record<string, unknown>): Record<string, unknown> & { filter?: string } => ({
    ...search,
    filter: typeof search.filter === 'string' ? search.filter : undefined,
  }),
  component: lazyRouteComponent(() => import('@puffin/system-templates/test-templates-page'), 'TestTemplatesPage'),
  staticData: { ...routeLabels('Test Templates'), navigationPath: '/tests/templates' },
});

const onPremiseRoute = createRoute({
  getParentRoute: () => testsRoute,
  path: 'onpremise',
  component: lazyRouteComponent(() => import('@puffin/onpremise/overview-page'), 'OverviewPage'),
  staticData: { ...routeLabels('Onpremise Machines'), navigationPath: '/tests/onpremise' },
});

const LazyMachineDetailsPage = lazy(async () => {
  const { MachineDetailsPage } = await import('@puffin/onpremise/machine-details-page');
  return { default: MachineDetailsPage };
});

const onPremiseDetailsRoute = createRoute(
  (() => {
    const useMachineName = (): string => `Machine: ${onPremiseDetailsRoute.useParams().instanceId}`;
    const DetailsPage = () => <LazyMachineDetailsPage instanceId={onPremiseDetailsRoute.useParams().instanceId} />;
    return {
      getParentRoute: () => testsRoute,
      path: 'onpremise/$instanceId',
      component: DetailsPage,
      staticData: {
        useBreadcrumbLabel: useMachineName,
        useDocumentTitlePrefix: useMachineName,
        navigationPath: '/tests/onpremise',
        parentBreadcrumbs: [{ useLabel: () => 'Onpremise Machines', to: '/tests/onpremise' }],
      },
    };
  })(),
);

export const testingRouteTree = testsRoute.addChildren([
  testsIndexRoute,
  testCasesRoute,
  testCaseDetailRoute,
  testPlansRoute,
  testPlanDetailRoute,
  testRunDetailRoute,
  startTestRoute,
  testTemplatesRoute,
  onPremiseRoute,
  onPremiseDetailsRoute,
]);
